// Action catalogue + gamepad-to-control mapping for manual (simulated) drone control.

export const AXIS_ACTIONS = [
  { id: "pitch", label: "Drone : Avancer / Reculer" },
  { id: "strafe", label: "Drone : Gauche / Droite" },
  { id: "yaw", label: "Drone : Yaw gauche / droite" },
  { id: "altitude", label: "Drone : Montée / Descente" },
];

export const BUTTON_ACTIONS = [
  { id: "hover", label: "Drone : Hover / Stop" },
  { id: "land_sim", label: "Drone : Atterrissage (sim)" },
  { id: "rtl_sim", label: "Drone : RTL urgence (sim)" },
  { id: "hold_position", label: "Drone : Tenir la position" },
  { id: "slow_mode", label: "Drone : Mode lent" },
  { id: "fast_mode", label: "Drone : Mode rapide" },
  { id: "throttle", label: "Drone : Poussée + (vitesse)" },
  { id: "brake", label: "Drone : Frein / Vitesse -" },
  { id: "camera_pan_left", label: "Caméra : Pan gauche" },
  { id: "camera_pan_right", label: "Caméra : Pan droite" },
  { id: "camera_tilt_up", label: "Caméra : Tilt haut" },
  { id: "camera_tilt_down", label: "Caméra : Tilt bas" },
  { id: "camera_zoom_in", label: "Caméra : Zoom +" },
  { id: "camera_zoom_out", label: "Caméra : Zoom -" },
  { id: "camera_center", label: "Caméra : Recentrer" },
  { id: "camera_mode", label: "Caméra : Changer de mode" },
  { id: "speed_up", label: "Drone : Vitesse +" },
  { id: "speed_down", label: "Drone : Vitesse -" },
];

export const ACTION_LABEL = Object.fromEntries(
  [...AXIS_ACTIONS, ...BUTTON_ACTIONS].map((a) => [a.id, a.label])
);
// Extra labels for journalled-only actions.
Object.assign(ACTION_LABEL, {
  move: "Drone : déplacement",
  sim_start: "Simulation démarrée",
  sim_stop: "Simulation arrêtée",
  sim_reset: "Position réinitialisée",
  camera_pan: "Caméra : pan",
  camera_tilt: "Caméra : tilt",
  camera_zoom: "Caméra : zoom",
  camera_zoom_reset: "Caméra : zoom réinitialisé",
  camera_mode_toggle: "Caméra : changement de mode",
  camera_recenter: "Caméra : recentrée",
  fullscreen_enter: "Plein écran activé",
  fullscreen_exit: "Plein écran quitté",
});

// Actions triggered on a button *press* (rising edge) rather than held.
export const DISCRETE_ACTIONS = [
  "land_sim", "rtl_sim", "hover", "hold_position",
  "camera_mode", "camera_center", "camera_recenter", "slow_mode", "fast_mode",
  "speed_up", "speed_down",
];

// Build the continuous control contribution from a gamepad snapshot + profile.
export function gamepadControl(snap, profile) {
  const c = {
    pitch: 0, roll: 0, yaw: 0, lift: 0,
    throttle: 0, brake: 0, camPan: 0, camTilt: 0, camZoom: 0, hover: false,
  };
  if (!snap || !profile) return c;
  const dz = profile.deadzone ?? 0.12;
  const invAlt = profile.invert?.altitude ? -1 : 1;
  const axis = (i) => {
    const v = snap.axes?.[i] ?? 0;
    return Math.abs(v) < dz ? 0 : v;
  };
  for (const [idx, action] of Object.entries(profile.axis_mapping || {})) {
    const v = axis(Number(idx));
    if (action === "pitch") c.pitch = -v;          // stick up = forward
    else if (action === "strafe") c.roll = v;
    else if (action === "yaw") c.yaw = v;
    else if (action === "altitude") c.lift = -v * invAlt; // stick up = climb
  }
  (snap.buttons || []).forEach((b, idx) => {
    const action = profile.button_mapping?.[String(idx)];
    if (!action) return;
    const pressed = b.pressed;
    if (action === "throttle") c.throttle = b.value || 0;
    else if (action === "brake") c.brake = b.value || 0;
    else if (action === "camera_pan_left" && pressed) c.camPan -= 1;
    else if (action === "camera_pan_right" && pressed) c.camPan += 1;
    else if (action === "camera_tilt_up" && pressed) c.camTilt += 1;
    else if (action === "camera_tilt_down" && pressed) c.camTilt -= 1;
    else if (action === "camera_zoom_in" && pressed) c.camZoom += 1;
    else if (action === "camera_zoom_out" && pressed) c.camZoom -= 1;
  });
  return c;
}
